<template>
    <modal name="call-sheet-notify" height="auto" :scrollable="true" class="modal-dialog modal-sm">
        <div class="modal-add-newJob" tabindex="-1" role="dialog" data-testid="csn-modal">
            <div role="document">
                <div class="modal-content o-common-modal-content">
                    <div class="modal-header u-no-border text-center o-small-modal-head">
                        <button @click="close()" type="button" class="close custom-modal--close" aria-label="Close">
                            <span aria-hidden="true">&times;</span>
                        </button>
                        <h4 class="modal-title">Notify — {{ dayLabel }}</h4>
                    </div>
                    <div class="modal-body c-common-box-shadow o-modal-body" v-loading.this.lock="sending">
                        <div class="row">
                            <div class="col-sm-12">
                                <!-- #1049 ed, 2024-05-14: "Select other groups or individuals to notify." -->
                                <p class="common-label-text common-label-text-block csn-instruction" data-testid="csn-instruction">
                                    Select other groups or individuals to notify.
                                    <span class="csn-rule">Recipients are the selected calls whose status is ticked.</span>
                                </p>

                                <!-- ed, 2026-09-30: the status filter, before the Whole-day box. Either or both. -->
                                <div class="csn-statuses" data-testid="csn-statuses">
                                    <span class="csn-statuses-label">Status:</span>
                                    <div class="checkbox o-custom-radio csn-status" data-testid="csn-status-confirmed">
                                        <label>
                                            <input type="checkbox" :checked="statuses.confirmed" @change="toggleStatus('confirmed', $event.target.checked)" data-testid="csn-status-confirmed-box">
                                            <span class="cr"><i class="cr-icon fa fa-check"></i></span>
                                            Confirmed
                                        </label>
                                    </div>
                                    <div class="checkbox o-custom-radio csn-status" data-testid="csn-status-unconfirmed">
                                        <label>
                                            <input type="checkbox" :checked="statuses.unconfirmed" @change="toggleStatus('unconfirmed', $event.target.checked)" data-testid="csn-status-unconfirmed-box">
                                            <span class="cr"><i class="cr-icon fa fa-check"></i></span>
                                            Unconfirmed
                                        </label>
                                    </div>
                                </div>

                                <div class="checkbox o-custom-radio csn-day" data-testid="csn-whole-day">
                                    <label>
                                        <input type="checkbox" :checked="selection.wholeDay" @change="toggleDay($event.target.checked)" data-testid="csn-whole-day-box">
                                        <span class="cr"><i class="cr-icon fa fa-check"></i></span>
                                        <strong>Whole day</strong> — every {{ statusWords }} call on {{ dayLabel }}
                                    </label>
                                </div>

                                <div class="csn-items">
                                    <div v-for="item in items" :key="item.key" class="checkbox o-custom-radio csn-item"
                                         :class="{ 'csn-item--none': !selectable(item) }" :data-testid="'csn-item-' + item.key">
                                        <label>
                                            <input type="checkbox" :disabled="!selectable(item)" :checked="isChecked(item)"
                                                   @change="toggleItem(item, $event.target.checked)" :data-testid="'csn-item-box-' + item.key">
                                            <span class="cr"><i class="cr-icon fa fa-check"></i></span>
                                            <span class="csn-item-when">{{ time(item.start) }}–{{ time(item.end) }}</span>
                                            <span class="csn-item-what">{{ item.position || '—' }}</span>
                                            <span class="csn-item-who" :title="names(item)">
                                                {{ item.confirmed }} confirmed · {{ item.unconfirmed }} unconfirmed<span v-if="item.total - item.confirmed - item.unconfirmed"> · {{ item.total - item.confirmed - item.unconfirmed }} other</span>
                                            </span>
                                        </label>
                                    </div>
                                </div>

                                <p class="csn-summary" data-testid="csn-summary">
                                    <span v-if="recipients.people">
                                        <strong>{{ recipients.people }}</strong> {{ recipients.people === 1 ? 'person' : 'people' }} will be notified
                                        <span v-if="recipients.duplicatesCollapsed"> (once each — {{ recipients.duplicatesCollapsed }} on more than one call)</span>.
                                    </span>
                                    <span v-else-if="!statuses.confirmed && !statuses.unconfirmed">Tick Confirmed and/or Unconfirmed to choose who is notified.</span>
                                    <span v-else>Nobody selected is {{ statusWords }}, so nobody will be notified.</span>
                                    <span v-if="recipients.otherStatus || recipients.unassigned" class="csn-muted">
                                        Skipped: {{ recipients.otherStatus }} with another status, {{ recipients.unassigned }} unassigned.
                                    </span>
                                </p>

                                <label class="common-label-text common-label-text-block">Message:</label>
                                <div class="o-sms-counter">{{ message.length }}/ {{ maxLength }}</div>
                                <textarea :maxlength="maxLength" class="form-control o-margin-bottom10" :class="{ 'error-border': problem && touched }"
                                          v-model="message" rows="4" placeholder="Enter your message here" @input="touched = true"
                                          data-testid="csn-message"></textarea>
                                <div v-if="problem && touched" class="err-msg" data-testid="csn-problem">{{ problem }}</div>
                                <!-- the pills, exactly as Batch Notify on the phone sheet shows them (phoneSheet.vue "Templates:") -->
                                <div data-testid="csn-templates">
                                    <label class="common-label-text">Templates:</label>
                                    <span v-for="(msg, inx) in templateMsgs" :key="inx">
                                        <a @click="addTemplateMsgs(msg)" class="ps-message-suggestions" :data-testid="'csn-template-' + inx">{{msg}}</a>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer text-center">
                        <ul class="o-filter-area">
                            <li>
                                <a href="javascript: void(0)" @click="close()" class="btn c-common-btn c-common-btn--large c-common-btn--inverse-icon-blue" data-testid="csn-cancel">
                                    <i class="fa fa-minus-circle" aria-hidden="true"></i> Cancel
                                </a>
                            </li>
                            <li>
                                <a href="javascript: void(0)" @click="send()" :class="{ 'isDisabled': !canSend }"
                                   class="btn c-solid-btn c-solid-btn--large c-solid-btn--green" data-testid="csn-send">
                                    <i class="fa fa-mobile" aria-hidden="true"></i> Send<span v-if="recipients.people"> to {{ recipients.people }}</span>
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </modal>
</template>

<script>
    // #1049 — Call Sheet Notify: the popup behind the phone icons on /viewCalls.
    //
    // ed, 2024-05-14: "When clicking on a cell phone for the notify to that day,
    // group, or individual, a pop up needs to display with instruction. Select
    // other groups or individuals to notify. CANCEL/SEND. If the master notify
    // icon for the day is selected then all other groups are unselected."
    // ed, 2026-09-30: a Confirmed / Unconfirmed status filter before the
    // Whole-day box (either or both); no "Include Call Info"; templates as
    // the phone sheet's Batch Notify pills. Web only.
    //
    // WHO is decided by ../../callSheetNotifySelection.js (pure, node-tested).
    // HOW it is sent is the phone sheet's own endpoints — one call →
    // job/individualNotify, more → job/selectedNotify — which since #2007 honour
    // the STOP preference and since #2010 refuse a caller or a call that is not
    // this client's. This component adds no send path and no server change.
    //
    // The parent view mounts it once and calls open() with the day's rows; it
    // asks the client record whether notify is switched on (batch_notify_flag,
    // the same flag the phone sheet's controls sit behind) and reports that
    // with the `allowed` event so the parent can show or hide the icons.
    import moment from 'moment';
    import service from '../../services.js';
    import { buildNotifyMessage } from '../../utils/notifyStats.js';
    import {
        itemsForDay, initialSelection, emptySelection, defaultStatuses, isSelectable,
        toggleWholeDay, toggleItem, toggleStatus, recipientsOf, sendPlan, messageProblem, MAX_MESSAGE_LENGTH,
    } from '../../callSheetNotifySelection.js';

    // the phone sheet's stock templates (phoneSheet.vue `tags`), shown when the client has none of its own
    const STOCK_TEMPLATES = [
        'Please login to Confirm',
        'TIME CHANGE Please Login',
        'SHORT NOTICE',
        'Call Dispatch ASAP',
        'Work Available',
        'Please RE-CONFIRM ASAP',
        'Please login to ConfirmPass or Decline',
        'Respond within',
        'This Offer expires within',
        'Please Confirm or Decline',
    ];

    export default {
        name: 'CallSheetNotify',
        data() {
            return {
                view: 'bulk',
                dayLabel: '',
                items: [],
                selection: emptySelection(),
                statuses: defaultStatuses(),
                message: '',
                templateMsgs: STOCK_TEMPLATES.slice(),
                touched: false,
                sending: false,
                maxLength: MAX_MESSAGE_LENGTH,
                lastResult: null,           // the last send's response body — for the spec and the parent
            };
        },
        computed: {
            recipients() { return recipientsOf(this.items, this.selection, this.statuses); },
            problem() { return messageProblem(this.message); },
            statusWords() {
                const on = ['confirmed', 'unconfirmed'].filter((s) => this.statuses[s]);
                return on.length ? on.join(' or ') : 'ticked-status';
            },
            canSend() { return !this.sending && this.recipients.people > 0 && this.problem === ''; },
            userToken() { return this.$store.getters.userToken; },
            userRole() { return this.$store.getters.userRole; },
            userClientID() { return this.$store.getters.userClientID; },
            userID() { return this.$store.getters.userID; },
        },
        created() {
            this.getClient();
        },
        methods: {
            /** Is Call Sheet Notify on for this client? The phone sheet's batch_notify_flag decides. */
            getClient() {
                service.get('client/' + this.userClientID, { 'Authorization': this.userToken }).then((res) => {
                    const r = (res && res.data && res.data.result) || {};
                    this.$emit('allowed', !!r.batch_notify_flag);
                    let own = [];
                    try { own = r.notify_templates ? JSON.parse(r.notify_templates) : []; } catch (e) { own = []; }
                    this.templateMsgs = Array.isArray(own) && own.length ? own : STOCK_TEMPLATES.slice();
                }).catch(() => { this.$emit('allowed', false); });
            },
            /**
             * @param {{view: 'bulk'|'individual', dayLabel: string, rows: Array, clicked: {type:'day'}|{type:'item', index:number}}} opts
             */
            open(opts) {
                this.view = opts.view;
                this.dayLabel = opts.dayLabel || '';
                this.items = itemsForDay(opts.rows, opts.view);
                const clicked = opts.clicked && opts.clicked.type === 'item'
                    ? { type: 'item', key: (this.items[opts.clicked.index] || {}).key }
                    : { type: 'day' };
                this.statuses = defaultStatuses();
                this.selection = initialSelection(this.items, clicked, this.statuses);
                this.message = '';
                this.touched = false;
                this.lastResult = null;
                this.$modal.show('call-sheet-notify');
            },
            close() { this.$modal.hide('call-sheet-notify'); },
            isChecked(item) { return this.selection.wholeDay || this.selection.keys.indexOf(item.key) >= 0; },
            selectable(item) { return isSelectable(item, this.statuses); },
            toggleDay(on) { this.selection = toggleWholeDay(this.selection, on); },
            toggleItem(item, on) { this.selection = toggleItem(this.items, this.selection, item.key, on, this.statuses); },
            toggleStatus(which, on) {
                const next = toggleStatus(this.items, this.selection, this.statuses, which, on);
                this.statuses = next.statuses;
                this.selection = next.selection;
            },
            addTemplateMsgs(msg) { this.message = msg; this.touched = true; },
            time(v) { return v ? moment(v).format('h:mm a') : ''; },
            names(item) { return item.calls.filter((c) => c.name).map((c) => c.name + ' (' + (c.status || 'no status') + ')').join(', '); },
            /** The phone sheet's endpoints, the phone sheet's headers. Nothing else. */
            send() {
                this.touched = true;
                if (!this.canSend) return;
                const plan = sendPlan(this.recipients, this.message, this.userID);
                if (!plan) return;
                this.sending = true;
                return service.post(plan.endpoint, plan.body, {
                    'Authorization': this.userToken,
                    'cs3-context-role': this.userRole,
                    'cs3-context-clientId': this.userClientID,
                }).then((res) => {
                    this.sending = false;
                    const body = (res && res.data) || {};
                    this.lastResult = { http: res && res.status, endpoint: plan.endpoint, body };
                    // #1764 — the status lives in the body. individualNotify has always answered
                    // status_code "0" on success with message "Notified"; a refusal comes back as
                    // HTTP 4xx and lands in catch. So a 2xx here is a send, and the body says how it went.
                    this.$store.dispatch('setHasPendingBatch', true);
                    this.$notify({ title: 'Notified', message: buildNotifyMessage(res), type: 'success' });
                    this.$emit('sent', this.lastResult);
                    this.close();
                }).catch((err) => {
                    this.sending = false;
                    const body = (err && err.response && err.response.data) || {};
                    this.lastResult = { http: err && err.response && err.response.status, endpoint: plan.endpoint, body };
                    this.$notify({ title: 'Not sent', message: body.status_message || body.message || 'The server refused the notify.', type: 'error' });
                    this.$emit('refused', this.lastResult);
                });
            },
        },
    };
</script>

<style scoped>
    .csn-instruction { margin-bottom: 8px; }
    .csn-rule { display: block; font-weight: normal; color: #6b6b6b; }
    .csn-statuses { display: flex; align-items: center; gap: 14px; margin: 6px 0 2px; }
    .csn-statuses-label { font-weight: bold; }
    .csn-status { margin: 0; }
    .csn-day { margin: 6px 0 4px; }
    .csn-items { max-height: 220px; overflow-y: auto; border: 1px solid #e6e6e6; border-radius: 3px; padding: 4px 8px; margin-bottom: 8px; }
    .csn-item label { display: flex; gap: 8px; align-items: baseline; }
    .csn-item--none label { opacity: .55; }
    .csn-item-when { white-space: nowrap; min-width: 118px; }
    .csn-item-what { flex: 1; }
    .csn-item-who { white-space: nowrap; color: #6b6b6b; font-size: 12px; }
    .csn-summary { margin: 4px 0 10px; }
    .csn-muted { display: block; color: #6b6b6b; font-size: 12px; }
    /* the phone sheet's pill (phoneSheet.vue .ps-message-suggestions is scoped there, so it is repeated here) */
    .ps-message-suggestions {
        font-size: 12px;
        padding: 2px;
        background-color: #55a2a2;
        color: white;
        border-radius: 4px;
        line-height: 2;
        margin: 2px;
        word-break: keep-all;
        word-wrap: keep;
        cursor: pointer;
    }
</style>
